/**
 * Theme Adapter
 * 
 * Implements the theme adapter for FreshCards integration with FreshVibe host APIs.
 * Provides theme synchronization and management capabilities.
 */

import { FreshVibeHost, FreshVibeThemeConfig } from '../../core/integration/freshvibe-host';
import { ThemeAdapter } from '../analysis/AdapterBlueprints';

/**
 * Theme Adapter Implementation
 * 
 * Bridges FreshCards theme management with FreshVibe host theming system.
 */
export class ThemeAdapterImpl implements ThemeAdapter {
  private host: FreshVibeHost;
  private currentTheme: FreshVibeThemeConfig;
  private themeHistory: FreshVibeThemeConfig[] = [];
  private pendingUpdates: Partial<FreshVibeThemeConfig>[] = [];
  private _isInitialized = false;
  private _isActive = false;
  private errorHandlers: Function[] = [];

  constructor(host: FreshVibeHost) {
    this.host = host;
    this.currentTheme = this.getDefaultTheme();
  }

  // Core Theme Operations
  async getTheme(): Promise<FreshVibeThemeConfig> {
    await this.ensureInitialized();
    return this.currentTheme;
  }

  async updateTheme(theme: Partial<FreshVibeThemeConfig>): Promise<void> {
    await this.ensureInitialized();
    
    // Validate theme before updating
    if (!this.validateTheme(theme)) {
      throw new Error('Invalid theme configuration');
    }

    // Sanitize theme data
    const sanitizedTheme = this.sanitizeTheme(theme);
    
    // Add to pending updates
    this.pendingUpdates.push(sanitizedTheme);
    
    // Send to host
    await this.sendThemeUpdate(sanitizedTheme);
    
    // Update local state
    this.currentTheme = { ...this.currentTheme, ...sanitizedTheme };
    this.addToHistory(this.currentTheme);
  }

  async resetTheme(): Promise<void> {
    await this.ensureInitialized();
    
    const defaultTheme = this.getDefaultTheme();
    await this.updateTheme(defaultTheme);
  }

  // Theme Synchronization
  async syncWithHost(): Promise<void> {
    await this.ensureInitialized();
    
    try {
      const hostTheme = this.host.getTheme();
      this.currentTheme = hostTheme;
      this.addToHistory(hostTheme);
      console.log('Theme synchronized with host');
    } catch (error) {
      this.handleError(error as Error);
      throw error;
    }
  }

  async syncWithFreshCards(): Promise<void> {
    await this.ensureInitialized();
    
    // Apply all pending updates
    for (const update of this.pendingUpdates) {
      await this.sendThemeUpdate(update);
      this.currentTheme = { ...this.currentTheme, ...update };
    }
    
    this.pendingUpdates = [];
    this.addToHistory(this.currentTheme);
  }

  // Theme Management
  getCurrentTheme(): FreshVibeThemeConfig {
    return this.currentTheme;
  }

  getThemeHistory(): FreshVibeThemeConfig[] {
    return [...this.themeHistory];
  }

  clearThemeHistory(): void {
    this.themeHistory = [];
  }

  // Theme Validation
  validateTheme(theme: Partial<FreshVibeThemeConfig>): boolean {
    // Basic validation - check for required color properties
    const requiredColors = ['primary', 'secondary', 'background', 'surface', 'text'];
    
    for (const color of requiredColors) {
      const colorValue = (theme as any)[color];
      if (colorValue && typeof colorValue !== 'string') {
        return false;
      }
    }

    // Validate font weight structure
    if (theme.fontWeight) {
      const fontWeights = ['light', 'normal', 'bold'];
      for (const weight of fontWeights) {
        const fontWeightValue = (theme.fontWeight as any)[weight];
        if (typeof fontWeightValue !== 'number') {
          return false;
        }
      }
    }

    return true;
  }

  sanitizeTheme(theme: Partial<FreshVibeThemeConfig>): Partial<FreshVibeThemeConfig> {
    const sanitized: Partial<FreshVibeThemeConfig> = {};

    // Copy valid color properties
    const colorProperties = ['primary', 'secondary', 'background', 'surface', 'text', 'muted', 'accent'];
    for (const prop of colorProperties) {
      const value = (theme as any)[prop];
      if (value && typeof value === 'string') {
        (sanitized as any)[prop] = value;
      }
    }

    // Copy valid typography properties
    if (theme.fontFamily && typeof theme.fontFamily === 'string') {
      sanitized.fontFamily = theme.fontFamily;
    }
    if (theme.fontSize && typeof theme.fontSize === 'string') {
      sanitized.fontSize = theme.fontSize;
    }
    if (theme.lineHeight && typeof theme.lineHeight === 'string') {
      sanitized.lineHeight = theme.lineHeight;
    }

    // Copy valid font weight properties
    if (theme.fontWeight) {
      sanitized.fontWeight = { light: 300, normal: 400, bold: 700 };
      for (const weight of ['light', 'normal', 'bold'] as const) {
        const value = (theme.fontWeight as any)[weight];
        if (typeof value === 'number') {
          (sanitized.fontWeight as any)[weight] = value;
        }
      }
    }

    // Copy valid spacing properties
    if (theme.spacing && typeof theme.spacing === 'object') {
      sanitized.spacing = theme.spacing;
    }

    return sanitized;
  }

  // Adapter Lifecycle
  async initialize(): Promise<void> {
    if (this._isInitialized) return;
    
    this._isInitialized = true;
    this._isActive = true;
    
    // Set up initial theme
    this.syncWithHost();
    
    console.log('ThemeAdapter initialized');
  }

  async destroy(): Promise<void> {
    this._isInitialized = false;
    this._isActive = false;
    this.pendingUpdates = [];
    this.errorHandlers = [];
    console.log('ThemeAdapter destroyed');
  }

  isInitialized(): boolean {
    return this._isInitialized;
  }

  isActive(): boolean {
    return this._isActive;
  }

  getState(): string {
    if (!this.isInitialized()) return 'uninitialized';
    if (!this.isActive()) return 'inactive';
    return this.pendingUpdates.length > 0 ? 'pending' : 'active';
  }

  // Configuration
  getConfig(): any {
    return {
      syncSettings: {
        autoSync: true,
        syncInterval: 1000,
        syncOnThemeChange: true,
        syncOnHostUpdate: true,
        conflictResolution: 'merge'
      },
      performance: {
        cacheSize: 1024,
        cacheTimeout: 5000,
        batchUpdateMs: 200,
        compressionThreshold: 1024,
        maxBatchSize: 100
      },
      errorHandling: {
        maxRetries: 3,
        retryDelay: 1000,
        backoffMultiplier: 2,
        fallbackData: {}
      },
      security: {
        encryption: false,
        compression: false,
        accessControl: true,
        auditLogging: false
      }
    };
  }

  updateConfig(config: any): void {
    // Update configuration logic here
    console.log('ThemeAdapter config updated', config);
  }

  // Error Handling
  onError(error: Error): void {
    this.handleError(error);
  }

  addErrorHandler(callback: Function): void {
    this.errorHandlers.push(callback);
  }

  removeErrorHandler(callback: Function): void {
    const index = this.errorHandlers.indexOf(callback);
    if (index > -1) {
      this.errorHandlers.splice(index, 1);
    }
  }

  // Private Methods
  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      this.initialize();
    }
  }

  private async sendThemeUpdate(theme: Partial<FreshVibeThemeConfig>): Promise<void> {
    try {
      this.host.updateTheme(theme);
    } catch (error) {
      this.handleError(error as Error);
      throw error;
    }
  }

  private addToHistory(theme: FreshVibeThemeConfig): void {
    this.themeHistory.push({ ...theme });
    // Keep only last 50 themes in history
    if (this.themeHistory.length > 50) {
      this.themeHistory.shift();
    }
  }

  private getDefaultTheme(): FreshVibeThemeConfig {
    return {
      primary: '#007bff',
      secondary: '#6c757d',
      background: '#ffffff',
      surface: '#f8f9fa',
      text: '#212529',
      muted: '#6c757d',
      accent: '#17a2b8',
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px',
      lineHeight: '1.5',
      fontWeight: {
        light: 300,
        normal: 400,
        bold: 700
      },
      spacing: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '32px',
        '2xl': '48px'
      },
      borderRadius: {
        sm: '4px',
        md: '8px',
        lg: '12px',
        xl: '16px'
      },
      shadows: {
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
      },
      transitions: {
       fast: '0.15s ease-in-out',
       normal: '0.3s ease-in-out',
       slow: '0.5s ease-in-out'
     }
   }
 }

 protected handleError(error: Error): void {
    console.error('ThemeAdapter error:', error);
    
    // Notify error handlers
    for (const handler of this.errorHandlers) {
      try {
        handler(error);
      } catch (handlerError) {
        console.error('Error in theme adapter error handler:', handlerError);
      }
    }
  }
}

// Export factory function
export function createThemeAdapter(host: FreshVibeHost): ThemeAdapterImpl {
  return new ThemeAdapterImpl(host);
}
// ─── Helpers used by EmbeddingAPIv2 (Phase 12 wiring stubs) ─────────────────
// These were imported by EmbeddingAPIv2 but never defined. Stubbed so the
// lib-mode build (strict module resolution) resolves without breaking runtime
// behaviour. The full implementations will land in a follow-up dispatch —
// for now they return sane defaults that match the host's expected Theme shape.

import type { Theme } from '../embedding/ThemeAPI';
import { DEFAULT_THEME } from '../embedding/ThemeAPI';

/**
 * Convert external design tokens (from a host platform) to a FreshCards Theme.
 * Stub: returns DEFAULT_THEME. TODO: full token-mapping implementation.
 */
export function externalTokensToTheme(
  tokens: Record<string, unknown>,
  options: { hostName?: string; mode?: 'light' | 'dark' | 'auto' } = {}
): Theme {
  // Phase 12 stub: defer full token mapping to a follow-up dispatch. The
  // host platform can still drive theme via the makeThemeAPI() factory
  // (which respects host config regardless of this stub's return value).
  return {
    ...DEFAULT_THEME,
    palette: {
      ...DEFAULT_THEME.palette,
      // Note: budget tokens from host if provided (best-effort passthrough).
      ...(tokens.brand ? { brand: String(tokens.brand) } : {}),
    },
  };
}

/**
 * Extract design tokens from CSS custom properties on the host document.
 * Stub: returns empty object. TODO: walk CSSStyleSheet rules + getComputedStyle.
 */
export function cssVarsToDesignTokens(
  doc: Document,
  hostName: string
): Record<string, string> {
  if (typeof doc === 'undefined' || !doc.documentElement) return {};
  const tokens: Record<string, string> = {};
  const styles = doc.documentElement.style;
  for (let i = 0; i < styles.length; i++) {
    const prop = styles.item(i);
    if (prop && prop.startsWith('--')) {
      tokens[prop] = styles.getPropertyValue(prop);
    }
  }
  return tokens;
}
